/**
 * QuoteCustomerLineItems — customer offer column (mock: Quote Summary).
 */

import React, { useMemo } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { CheckCircleOutline, PictureAsPdf } from "@mui/icons-material";
import {
  formatQuoteMoney,
  getCustomerLineItems,
  getQuoteTotal,
  getQuoteWhatsIncluded,
} from "../services/quoteOperationalStatus.js";

const QuoteCustomerLineItems = ({
  quote,
  onPreviewPdf,
  pdfReady = false,
  pdfBusy = false,
}) => {
  const lines = useMemo(() => getCustomerLineItems(quote), [quote]);
  const total = useMemo(() => getQuoteTotal(quote), [quote]);
  const included = useMemo(() => getQuoteWhatsIncluded(quote), [quote]);

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
      }}
      data-testid="quote-customer-line-items"
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          gap={1}
          mb={1.5}
          flexWrap="wrap"
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Quote summary (customer)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Visible on PDF / customer link
            </Typography>
          </Box>
          {onPreviewPdf && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={onPreviewPdf}
              disabled={!pdfReady || pdfBusy}
            >
              {pdfBusy ? "Preparing…" : "Preview as PDF"}
            </Button>
          )}
        </Box>

        {lines.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No priced line items yet.
          </Typography>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ mb: 2, borderRadius: 1.5 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, width: 110 }}>
                    Amount
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((line, index) => (
                  <TableRow key={`${line.description}-${index}`}>
                    <TableCell>{line.description}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatQuoteMoney(line.amount)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ fontWeight: 800 }}>TOTAL</TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontWeight: 800, color: "success.dark" }}
                  >
                    {formatQuoteMoney(total)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          What&apos;s included
        </Typography>
        <List dense disablePadding>
          {included.map((item) => (
            <ListItem key={item} disableGutters sx={{ py: 0.25 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleOutline color="success" sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>

        <Box
          mt={2}
          p={1.5}
          borderRadius={1.5}
          bgcolor="grey.50"
          border="1px solid"
          borderColor="divider"
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Terms
          </Typography>
          <Typography variant="body2">
            Quote valid for 7 days from issue unless otherwise agreed. Deposit
            secures the booking date.
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuoteCustomerLineItems;
